import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { siteInfo } from '../data/mockData';

const FunerariaMeier = () => {
  const servicos = [
    "Velório 24 horas",
    "Cremação completa",
    "Sepultamento",
    "Translado nacional e internacional",
    "Tanatopraxia profissional",
    "Documentação completa",
    "Urnas variadas",
    "Flores e coroas",
    "Cerimônias religiosas",
    "Apoio psicológico ao luto",
    "Planos funerários",
    "Assistência jurídica"
  ];

  const bairrosAtendidos = [
    "Meier", "Engenho Novo", "Todos os Santos", "Cachambi",
    "Engenho de Dentro", "Abolição", "Pilares", "São Francisco Xavier",
    "Rocha", "Riachuelo", "Sampaio", "Jacaré",
    "Maria da Graça", "Del Castilho", "Inhaúma", "Tomás Coelho"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero SEO */}
      <section className="relative bg-gradient-to-r from-teal-600 to-lime-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Funerária no Meier - Rio de Janeiro
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-teal-50">
              Santa Casa Serviço Funerário Imediato - Atendimento 24 Horas na Zona Norte
            </p>
            <p className="text-lg mb-8 max-w-3xl mx-auto">
              Há mais de 10 anos oferecendo serviços funerários completos no Meier e Zona Norte do Rio de Janeiro
              com respeito, dignidade e atendimento humanizado.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
                className="inline-flex items-center justify-center bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-md text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Ligar Agora: {siteInfo.phone}
              </a>
              <Link
                to="/contato"
                className="inline-flex items-center justify-center bg-lime-500 hover:bg-lime-600 text-white px-8 py-4 rounded-md text-lg font-semibold transition-all duration-300"
              >
                Entre em Contato
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Localização Destaque */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-teal-50 rounded-lg p-8 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <MapPin className="w-12 h-12 text-teal-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Endereço</h3>
                <p className="text-gray-700">{siteInfo.address}</p>
                <p className="text-gray-600 text-sm">CEP: {siteInfo.cep}</p>
              </div>
              <div>
                <Clock className="w-12 h-12 text-teal-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Horário</h3>
                <p className="text-gray-700 font-bold">24 Horas</p>
                <p className="text-gray-600 text-sm">Todos os dias da semana</p>
              </div>
              <div>
                <Phone className="w-12 h-12 text-teal-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Telefone</h3>
                <p className="text-gray-700 font-bold">{siteInfo.phone}</p>
                <p className="text-gray-600 text-sm">Atendimento imediato</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Principal */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Funerária no Meier: Serviços Completos com Atendimento 24 Horas
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-700 mb-4">
                  A <strong>Santa Casa Serviço Funerário Imediato</strong> é a <strong>funerária de referência no Meier</strong> e em toda a 
                  <strong> Zona Norte do Rio de Janeiro</strong>. Localizada na <strong>Rua Dias da Cruz, 215</strong>, oferecemos 
                  <strong> atendimento 24 horas</strong> com equipe especializada pronta para auxiliar sua família em momentos difíceis.
                </p>
                
                <p className="text-gray-700 mb-4">
                  Como <strong>funerária no Meier</strong>, entendemos a importância de estar próximo às famílias da região. 
                  Nosso compromisso vai além dos serviços funerários tradicionais: oferecemos acolhimento, suporte emocional 
                  e toda a assistência necessária para que você possa se concentrar no que realmente importa - honrar a memória 
                  do seu ente querido.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mt-6 mb-4">
                  Por Que Escolher Nossa Funerária no Meier?
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Localização estratégica no Meier:</strong> Fácil acesso de toda Zona Norte
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Atendimento 24h todos os dias:</strong> Sempre disponíveis quando você precisa
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Mais de 18.000 famílias atendidas:</strong> Experiência e tradição comprovadas
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Equipe humanizada:</strong> Profissionais treinados para oferecer apoio emocional
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Serviços completos:</strong> Do velório ao sepultamento ou cremação
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    Serviços Funerários no Meier
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {servicos.map((servico, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-lime-500 mr-2 mt-1 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{servico}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-teal-500 to-lime-500 rounded-2xl shadow-lg p-6 text-white">
                  <h3 className="text-xl font-semibold mb-3">Precisa de Atendimento Imediato?</h3>
                  <p className="mb-4">
                    Nossa equipe está pronta para atendê-lo 24 horas por dia, 7 dias por semana.
                  </p>
                  <a
                    href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
                    className="inline-block w-full bg-white text-teal-600 hover:bg-gray-100 px-6 py-3 rounded-md font-semibold text-center transition-colors"
                  >
                    Ligar Agora: {siteInfo.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bairros Atendidos */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Funerária Atendendo Toda Zona Norte do Rio de Janeiro
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Nossa funerária no Meier atende famílias de toda a Zona Norte com rapidez e eficiência. 
            Estamos estrategicamente localizados para oferecer atendimento imediato.
          </p>
          <div className="bg-gray-50 rounded-lg p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              Principais Bairros Atendidos:
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {bairrosAtendidos.map((bairro, index) => (
                <div key={index} className="bg-white rounded-lg p-3 shadow text-center hover:shadow-md transition-shadow">
                  <span className="text-gray-700 font-medium">{bairro}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-600 mt-6">
              E toda a região da <strong>Zona Norte do Rio de Janeiro</strong>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ SEO */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Perguntas Frequentes sobre Funerária no Meier
          </h2>
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Qual o endereço da funerária no Meier?
              </h3>
              <p className="text-gray-700">
                A Santa Casa Serviço Funerário Imediato está localizada na <strong>Rua Dias da Cruz, 215 - Meier, Rio de Janeiro - RJ, CEP 20725-001</strong>. 
                Estamos próximos ao metrô e trem do Meier, com fácil acesso de carro e transporte público.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                A funerária funciona 24 horas?
              </h3>
              <p className="text-gray-700">
                Sim! Nossa <strong>funerária no Meier funciona 24 horas por dia</strong>, todos os dias da semana, incluindo finais de semana e feriados. 
                Você pode nos ligar a qualquer momento no telefone <strong>{siteInfo.phone}</strong>.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Quais serviços a funerária do Meier oferece?
              </h3>
              <p className="text-gray-700">
                Oferecemos serviços funerários completos: <strong>velório, sepultamento, cremação, translado nacional e internacional, 
                tanatopraxia, documentação, urnas, flores, cerimônias religiosas, apoio ao luto e planos funerários</strong>.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Quanto custa um serviço funerário no Meier?
              </h3>
              <p className="text-gray-700">
                Os valores variam de acordo com o tipo de serviço escolhido (sepultamento ou cremação), urna, flores e outros itens. 
                Entre em contato conosco para um orçamento personalizado sem compromisso. Trabalhamos com total transparência nos valores.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                A funerária faz translado para outros estados?
              </h3>
              <p className="text-gray-700">
                Sim! Realizamos <strong>translado funerário nacional e internacional</strong>. Cuidamos de toda a documentação, 
                logística e protocolos necessários para o transporte seguro e digno.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Funerária no Meier - Estamos Aqui Para Você
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Atendimento imediato 24 horas. Nossa equipe está pronta para oferecer todo o suporte que sua família precisa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
              className="inline-flex items-center justify-center bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-md text-lg font-semibold transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              Ligar Agora
            </a>
            <Link
              to="/contato"
              className="inline-flex items-center justify-center bg-lime-500 hover:bg-lime-600 text-white px-8 py-4 rounded-md text-lg font-semibold transition-all duration-300"
            >
              Formulário de Contato
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FunerariaMeier;